import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
`;

export const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
      circle at 20% 30%,
      rgba(255, 99, 233, 0.3),
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(92, 154, 255, 0.25),
      transparent 50%
    ),
    ${({ theme }) => theme.backgroundColor.dark};
  z-index: 9999;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.05) 1px,
        transparent 1px
      ),
      linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 100px 100px;
    opacity: 0.3;
    animation: ${fadeIn} 1s ease-in;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(41, 41, 50, 0.9),
      rgba(15, 15, 20, 0.85)
    );
    z-index: -1;
  }
`;

export const Particles = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;

  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: linear-gradient(135deg, #8eaaff 0%, #ff48fb 100%);
    border-radius: 50%;
    opacity: 0;

    &:nth-child(1) {
      left: 10%;
      top: 20%;
      animation-delay: 0s;
    }
    &:nth-child(2) {
      left: 20%;
      top: 80%;
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      left: 30%;
      top: 40%;
      animation-delay: 0.4s;
    }
    &:nth-child(4) {
      left: 40%;
      top: 10%;
      animation-delay: 0.6s;
    }
    &:nth-child(5) {
      left: 50%;
      top: 60%;
      animation-delay: 0.8s;
    }
    &:nth-child(6) {
      left: 60%;
      top: 30%;
      animation-delay: 1s;
    }
    &:nth-child(7) {
      left: 70%;
      top: 70%;
      animation-delay: 1.2s;
    }
    &:nth-child(8) {
      left: 80%;
      top: 15%;
      animation-delay: 1.4s;
    }
    &:nth-child(9) {
      left: 90%;
      top: 50%;
      animation-delay: 1.6s;
    }
    &:nth-child(10) {
      left: 15%;
      top: 90%;
      animation-delay: 1.8s;
    }
    &:nth-child(11) {
      left: 25%;
      top: 5%;
      animation-delay: 2s;
    }
    &:nth-child(12) {
      left: 75%;
      top: 95%;
      animation-delay: 2.2s;
    }
  }
`;

export const LoadingContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem;
  animation: ${fadeIn} 0.8s ease-in;
`;

export const LoadingLogo = styled.div`
  width: 200px;
  height: 200px;
  animation: ${float} 3s ease-in-out infinite;
  filter: drop-shadow(0 10px 30px rgba(255, 99, 233, 0.3));
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  & > * {
    width: 100% !important;
    height: 100% !important;
  }

  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
  }
`;

export const LoadingText = styled.div`
  text-align: center;
  animation: ${fadeIn} 0.8s ease-in;

  h2 {
    font-size: clamp(2rem, 5vw, 3rem);
    color: ${({ theme }) => theme.color.light};
    margin: 0 0 0.5rem 0;
    font-weight: 700;
    background: ${({ theme }) => theme.backgroundColor.orange};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    font-size: 1rem;
    color: ${({ theme }) => theme.color.lightGray};
    margin: 0;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.8;
  }
`;

export const LoadingBar = styled.div`
  width: 300px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    width: 250px;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: ${pulse} 1.5s ease-in-out infinite;
  }
`;

export const LoadingProgress = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.backgroundColor.orange};
  border-radius: 10px;
  transition: width 0.3s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );
    animation: ${pulse} 1s ease-in-out infinite;
  }
`;

export const LoadingSpinner = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;

  .spinner-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 3px solid transparent;
    border-top-color: ${({ theme }) => theme.CTA.secondary};
    border-radius: 50%;
    animation: ${rotate} 1s linear infinite;

    &:nth-child(1) {
      width: 60px;
      height: 60px;
      border-top-color: ${({ theme }) => theme.CTA.secondary};
      animation-duration: 1s;
    }

    &:nth-child(2) {
      width: 45px;
      height: 45px;
      border-top-color: rgba(255, 99, 233, 0.6);
      animation-duration: 1.5s;
      animation-direction: reverse;
    }

    &:nth-child(3) {
      width: 30px;
      height: 30px;
      border-top-color: rgba(92, 154, 255, 0.6);
      animation-duration: 0.8s;
    }
  }
`;

